/**
 * `industry-category` - the main category the register's industry suggests
 * for the receipt, with no model: `Restaurangverksamhet` is eating out.
 *
 * Needs `categories` (the key must be on the list) and uses the state's
 * `company` when a `company` step found one. Provides
 * `candidates:receiptCategory`. No options.
 */

import type { Step } from '../types.js';
import { summarize } from './support.js';

/**
 * SNI industry descriptions (Swedish) to the main category a purchase there
 * usually is. First match wins; an industry not listed adds nothing.
 */
const INDUSTRY_GROUPS: [RegExp, string][] = [
  [/restaurang|café|cafe|bar(verksamhet)?\b|catering|servering|gatukök|pizzeri|snabbmat/i, 'restaurants_and_cafes'],
  [/livsmedel|dagligvaror|stormarknad|varuhus.*mest livsmedel|kött|fisk|bageri|frukt|grönsak|konfektyr|drycker/i, 'groceries'],
  [/drivmedel|bensin|fordon|bil(ar|verkstad|reparation|tvätt)?\b|motorfordon|parkering|kollektivtrafik|taxi|järnväg/i, 'transport'],
  [/järn|bygg|trävaror|färg|trädgård|verktyg|golv|vvs/i, 'diy_and_garden'],
  [/kläder|skor|textil|konfektion|lädervaror|väskor|ur och guld/i, 'clothing_and_fashion'],
  [/dator|elektronik|telekommunikation|hemelektronik|ljud och bild|mobiltelefon/i, 'electronics'],
  [/apotek|farmaceutisk|kosmetika|hygien|parfym|hälsa|frisör|skönhet|optik/i, 'health_and_beauty'],
  [/möbler|belysning|hushållsartiklar|husgeråd|inredning|glas|porslin|järnhandel med hushåll|heminredning|tvätt/i, 'home_and_household'],
  [/sport|fritid|leksak|spel|böcker|musik|hobby|kultur|nöjes|biograf|museum|lotteri/i, 'leisure_and_entertainment'],
  [/hotell|logi|camping|resebyrå|researrangör|flyg/i, 'trips_and_lodging'],
];

/** The main category an SNI industry description suggests, or null when it is not listed. */
export function industryMainCategory(industry: string | null | undefined): string | null {
  if (!industry) return null;
  return INDUSTRY_GROUPS.find(([pattern]) => pattern.test(industry))?.[1] ?? null;
}

/** The main category the register's industry suggests for the receipt, with no model. */
export const industryCategoryStep: Step<Record<string, never>> = {
  name: 'industry-category',
  needs: () => ['categories'],
  provides: () => ['candidates:receiptCategory'],
  async run(state, context) {
    const industry = state.company?.industry;
    if (!industry) return { skipped: 'no industry' };
    const key = industryMainCategory(industry);
    if (!key || !state.input.categories.some((category) => category.key === key)) return { skipped: `no group for "${industry}"` };
    const candidates = { receiptCategory: [{ value: key, source: context.id }] };
    return { candidates, added: summarize(candidates) };
  },
};
